import { isPublicFormRequestFieldType } from './matterPublicForms';

const normalizeCaseId = (value) => {
    const normalized = String(value ?? '').trim();
    if (!/^[1-9][0-9]*$/.test(normalized)) return null;

    const numeric = Number(normalized);
    if (!Number.isSafeInteger(numeric) || numeric <= 0) return null;
    return String(numeric);
};

export const readCaseBoundHiddenFieldCapability = (payload, currentCaseId) => {
    const expectedCaseId = normalizeCaseId(currentCaseId);
    const permissions = payload?.permissions;

    if (
        expectedCaseId === null
        || !permissions
        || typeof permissions !== 'object'
        || Array.isArray(permissions)
        || normalizeCaseId(permissions.case_id) !== expectedCaseId
    ) {
        return null;
    }

    const canViewHiddenFields = permissions.can_view_hidden_fields === true;

    return {
        case_id: expectedCaseId,
        can_view_hidden_fields: canViewHiddenFields,
        can_edit_hidden_fields: canViewHiddenFields
            && permissions.can_edit_hidden_fields === true
    };
};

export const canViewHiddenFieldsForCase = (capability, currentCaseId) => {
    const expectedCaseId = normalizeCaseId(currentCaseId);
    return expectedCaseId !== null
        && normalizeCaseId(capability?.case_id) === expectedCaseId
        && capability?.can_view_hidden_fields === true;
};

export const canEditHiddenFieldsForCase = (capability, currentCaseId) => {
    const expectedCaseId = normalizeCaseId(currentCaseId);
    return expectedCaseId !== null
        && normalizeCaseId(capability?.case_id) === expectedCaseId
        && capability?.can_edit_hidden_fields === true;
};

const normalizeFieldState = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (value === 0 || value === 1 || value === 2) return value;
    if (value === '0' || value === '1' || value === '2') return Number(value);
    return null;
};

const getCustomFieldById = (fields, fieldId) => {
    const normalizedFieldId = normalizeCaseId(fieldId);
    if (normalizedFieldId === null) return null;

    return (Array.isArray(fields) ? fields : []).find(
        field => normalizeCaseId(field?.id) === normalizedFieldId
    ) || null;
};

export const isCustomFieldVisible = (field, capability, currentCaseId) => {
    if (!field || typeof field !== 'object') return false;

    const hidden = normalizeFieldState(field.hidden);
    const obsolete = normalizeFieldState(field.obsolete);
    if (hidden === null || obsolete !== 0) return false;
    if (hidden === 1) return canViewHiddenFieldsForCase(capability, currentCaseId);
    return hidden === 0;
};

export const isCustomFieldEditable = (field, capability, currentCaseId) => {
    if (!isCustomFieldVisible(field, capability, currentCaseId)) return false;
    return normalizeFieldState(field.hidden) !== 1
        || canEditHiddenFieldsForCase(capability, currentCaseId);
};

const filterCustomFieldRecord = (
    record,
    fields,
    capability,
    currentCaseId,
    { editableOnly = false, preserveMetadata = false, valueBearingOnly = false } = {}
) => {
    if (!record || typeof record !== 'object' || Array.isArray(record)) return {};

    return Object.fromEntries(
        Object.entries(record).filter(([key]) => {
            if (!/^[0-9]+$/.test(key)) return preserveMetadata;
            const field = getCustomFieldById(fields, key);
            if (valueBearingOnly && isPublicFormRequestFieldType(field?.field_id)) return false;
            return editableOnly
                ? isCustomFieldEditable(field, capability, currentCaseId)
                : isCustomFieldVisible(field, capability, currentCaseId);
        })
    );
};

export const filterCustomFieldValuesForCase = (
    values,
    fields,
    capability,
    currentCaseId,
    { editableOnly = false, valueBearingOnly = false } = {}
) => filterCustomFieldRecord(
    values,
    fields,
    capability,
    currentCaseId,
    { editableOnly, preserveMetadata: false, valueBearingOnly }
);

export const filterCustomFieldRecordForCase = (
    record,
    fields,
    capability,
    currentCaseId,
    { editableOnly = false } = {}
) => filterCustomFieldRecord(
    record,
    fields,
    capability,
    currentCaseId,
    { editableOnly, preserveMetadata: true }
);

export const sanitizeCustomFieldDraftForCase = (
    draft,
    fields,
    capability,
    currentCaseId
) => {
    if (!draft || typeof draft !== 'object' || Array.isArray(draft)) return {};

    return {
        ...draft,
        ...(Object.prototype.hasOwnProperty.call(draft, 'field_values')
            ? {
                field_values: filterCustomFieldValuesForCase(
                    draft.field_values,
                    fields,
                    capability,
                    currentCaseId,
                    { editableOnly: true, valueBearingOnly: true }
                )
            }
            : {})
    };
};

export const customFieldSelectorIsVisibleForCase = (
    selector,
    fields,
    capability,
    currentCaseId
) => {
    if (selector === null || selector === undefined || selector === '') return true;

    const normalizedSelector = String(selector);
    if (['updated_at', 'created_at', 'field_name', 'value', 'user_display'].includes(normalizedSelector)) {
        return true;
    }

    return isCustomFieldVisible(
        getCustomFieldById(fields, normalizedSelector),
        capability,
        currentCaseId
    );
};

export const customFieldRecordContainsReadonlyValues = (
    record,
    fields,
    capability,
    currentCaseId
) => Object.keys(record && typeof record === 'object' && !Array.isArray(record) ? record : {})
    .filter(key => /^[0-9]+$/.test(key))
    .some(key => {
        const field = getCustomFieldById(fields, key);
        return isCustomFieldVisible(field, capability, currentCaseId)
            && !isCustomFieldEditable(field, capability, currentCaseId);
    });

export const sanitizeCustomFieldExportPayloadForCase = (
    payload,
    fields,
    capability,
    currentCaseId
) => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;

    const visibleFieldIds = new Set(
        (Array.isArray(fields) ? fields : [])
            .filter(field => isCustomFieldVisible(field, capability, currentCaseId))
            .map(field => normalizeCaseId(field?.id))
            .filter(Boolean)
    );
    const fieldMap = Object.fromEntries(
        Object.entries(payload.fieldMap || {}).filter(([fieldId, fieldName]) => (
            visibleFieldIds.has(normalizeCaseId(fieldId))
            && typeof fieldName === 'string'
            && fieldName.trim() !== ''
        ))
    );
    const allowedColumnNames = new Set([
        ...Object.values(fieldMap),
        'Created At',
        'Last Updated At',
        'Last Updated By'
    ]);

    return {
        ...payload,
        fieldMap,
        fieldTypes: Object.fromEntries(
            Object.entries(payload.fieldTypes || {}).filter(([fieldId]) => (
                Object.prototype.hasOwnProperty.call(fieldMap, fieldId)
            ))
        ),
        data: (Array.isArray(payload.data) ? payload.data : [])
            .filter(row => row && typeof row === 'object' && !Array.isArray(row))
            .map(row => Object.fromEntries(
                Object.entries(row).filter(([columnName]) => allowedColumnNames.has(columnName))
            ))
    };
};

export const filterCustomFieldUpdatesForCase = (
    updates,
    fields,
    capability,
    currentCaseId
) => {
    return (Array.isArray(updates) ? updates : [])
        .filter(update => update && typeof update === 'object' && !Array.isArray(update))
        .map(update => filterCustomFieldRecordForCase(
            update,
            fields,
            capability,
            currentCaseId
        ));
};
